import { CheckBadgeIcon } from '@heroicons/react/24/solid'
import { BiSave } from 'react-icons/bi'
import { useShallow } from 'zustand/react/shallow'

import { Button } from '@ui/components/core'
import { PanelEnum, Pipeline } from '@ui/interfaces'
import useSaveClick from '@ui/lib/hooks/useSaveClick'
import { GetPipelineListQuery } from '@ui/loaders/processor'
import { useCodeNodeStore, useNodeAndEdgeStore, useSaveUpdateStatusStore } from '@ui/stores'

export function SaveToNewOrExistingPipeline({ setSaveTabClosed }: { setSaveTabClosed: () => void }) {
  const { data } = GetPipelineListQuery()

  const fetchedData = data

  const code = useCodeNodeStore(useShallow((state) => state.code))
  const setZustandCode = useCodeNodeStore(useShallow((state) => state.setCode))

  const previouslySavedDescription = useNodeAndEdgeStore(useShallow((state) => state.getCurrentNodeKey('description')))
  const setDescription = useCodeNodeStore(useShallow((state) => state.setDescription))
  const description = useCodeNodeStore(useShallow((state) => state.description))
  const currentNodeId = useNodeAndEdgeStore(useShallow((state) => state.currentNodeId)) as string
  const updateNodeKeyValue = useNodeAndEdgeStore(useShallow((state) => state.updateNodeKeyValue))

  const setPipelineModified = useSaveUpdateStatusStore(useShallow((state) => state.setPipelineModified))
  const setCurrentNodeId = useNodeAndEdgeStore(useShallow((state) => state.setCurrentNodeId))

  const setDisplayModal = useNodeAndEdgeStore(useShallow((state) => state.setDisplayModal))

  const createNextNodes = useSaveClick(currentNodeId)
  const onSave = () => {
    setDisplayModal(PanelEnum.HIDDEN)
    setSaveTabClosed()
    createNextNodes()
    updateNodeKeyValue(currentNodeId, {
      modified: true,
      code: code || '',
      description: description || previouslySavedDescription,
    })
    setPipelineModified(true)
    setCurrentNodeId(null)
    setZustandCode('')
    setDescription('')
  }

  const onDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setDescription(value)
  }

  return (
    <>
      <div className={'ml-5 mt-5 flex flex-col bg-white'}>
        <div className={'flex-1 text-lg'}>Save code</div>
        <div className={'flex-1 text-base text-gray-500'}>A code node will be added to your pipeline after saving</div>
        <div className={'mt-5 flex-1'}>
          <label htmlFor="name" className="block text-sm font-medium leading-6 text-gray-900">
            Description
          </label>

          <div className="mt-2">
            <input
              type="text"
              name="name"
              id="name"
              defaultValue={description || 'A JS function'}
              onChange={onDescriptionChange}
              value={description}
              data-testid={'code-save-description'}
              className="block w-full rounded-md border-0 bg-gray-100 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-400 sm:text-sm sm:leading-6"></input>
          </div>
        </div>

        <div className={'mt-4 flex-1 border-t-2 border-gray-200'} />

        <div className={'mt-4 flex-1'}>
          <div className={'text-lg'}>Add to a pipeline</div>
        </div>
        <div className={'mt-3 flex flex-1 flex-row gap-10'}>
          {fetchedData &&
            fetchedData.getLoafList.map((pipeline: Pipeline) => {
              return (
                <div
                  key={pipeline.id}
                  className={'w-1/4 rounded-md border-x border-b border-t-4 border-primary-600 px-3 py-2'}>
                  <div className={'flex flex-row justify-between'}>
                    <div>{pipeline.name}</div>
                    <div>
                      <CheckBadgeIcon className={'h-5 w-5 text-primary-600'}></CheckBadgeIcon>
                    </div>
                  </div>
                  <div className={'flex flex-row justify-between'}>
                    <div className={'text-sm text-gray-500'}>2 destinations</div>
                  </div>
                  <div className={'mt-2 flex flex-row justify-between'}>
                    <div className={'flex-1'}>
                      <CheckBadgeIcon className={'inline-block h-8 w-8'} />{' '}
                      <CheckBadgeIcon className={'inline-block h-8  w-8'} />
                    </div>
                    <div className={'flex flex-1 items-end'}>
                      <div className={'ml-14 w-fit flex-1 rounded-md bg-green-500 pl-2.5 pr-1.5 text-sm'}>Enabled</div>
                    </div>
                  </div>
                  <div className={'mt-2 flex flex-1 flex-row items-end'}>
                    <div className={'flex-1 break-keep text-right text-xs text-gray-500'}>
                      Last updated: Dec 6, 2023
                    </div>
                  </div>
                </div>
              )
            })}

          <div className={'w-1/4 rounded-md border-x border-b border-t-4 border-green-600 px-3 py-2'}>
            <div className={'flex flex-row justify-between'}>
              <div>Pipeline name</div>
            </div>
            <div className={'flex flex-row justify-between'}>
              <div className={'text-sm text-gray-500'}>2 destinations</div>
            </div>
            <div className={'mt-2 flex flex-row justify-between'}>
              <div className={'flex-1'}>
                <CheckBadgeIcon className={'inline-block h-8 w-8'} />{' '}
                <CheckBadgeIcon className={'inline-block h-8  w-8'} />{' '}
              </div>
              <div className={'flex flex-1 items-end'}>
                <div className={'ml-14 w-fit flex-1 rounded-md bg-green-500 pl-2.5 pr-1.5 text-sm'}>Enabled</div>
              </div>
            </div>
            <div className={'mt-2 flex flex-1 flex-row items-end'}>
              <div className={'flex-1 break-keep text-right text-xs text-gray-500'}>Last updated: Dec 6, 2023</div>
            </div>
          </div>
        </div>

        <div className={'flex-1'}>
          <Button variant={'primary'} onClick={onSave} disabled={description == ''} data-testid={'code-save-button'}>
            <BiSave className={'mb-0.5 mr-1.5 inline-block h-5 w-5'} />
            Save
          </Button>
        </div>
      </div>
    </>
  )
}
